import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  UserPlus,
  AlertCircle,
  Send,
  RefreshCw,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  BookOpen,
  ChevronDown,
  Save,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const StudentRegistrationPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      gender: "male",
      address: "",
      city: "",
      state: "",
      pincode: "",
      enrollmentDate: "",
      selectedSubjects: [],
      guardianName: "",
      guardianPhone: "",
      guardianRelation: "father",
      previousSchool: "",
      profileImage: "",
    },
  });

  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageSize: 100 },
      });
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
      console.error("Error fetching subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        selectedSubjects: Array.isArray(data.selectedSubjects)
          ? data.selectedSubjects.map((id) => Number(id))
          : [],
        dateOfBirth: new Date(data.dateOfBirth).toISOString(),
        enrollmentDate: new Date(data.enrollmentDate).toISOString(),
        profileImage: data.profileImage || "string", // Default as per requirement
      };

      await axios.post(`${server}/student/new`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Student registered successfully!");
      reset();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to register student"
      );
      console.error("Error registering student:", error);
    }
  };

  return (
    <motion.div
      className="max-w-5xl mx-auto p-6 space-y-8 bg-gray-900 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 bg-gradient-to-br from-green-600 to-blue-600 rounded-2xl shadow-lg shadow-green-500/20">
            <UserPlus className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-br from-green-400 to-blue-400 bg-clip-text text-transparent">
            Student Registration
          </h1>
        </div>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Register new students to the institute
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Personal Information */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-green-400" />
            Personal Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                First Name *
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="Enter first name"
                {...register("firstName", {
                  required: "First name is required",
                })}
              />
              {errors.firstName && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.firstName.message}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="Enter last name"
                {...register("lastName", { required: "Last name is required" })}
              />
              {errors.lastName && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.lastName.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <Mail className="w-4 h-4" />
                Email *
              </label>
              <input
                type="email"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="student@example.com"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <Phone className="w-4 h-4" />
                Phone *
              </label>
              <input
                type="tel"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="+91 9876543210"
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^[+]?[\d\s-]{10,}$/,
                    message: "Invalid phone number",
                  },
                })}
              />
              {errors.phone && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <Calendar className="w-4 h-4" />
                Date of Birth *
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-gray-900 text-white"
                {...register("dateOfBirth", {
                  required: "Date of birth is required",
                })}
              />
              {errors.dateOfBirth && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.dateOfBirth.message}
                </p>
              )}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Gender *
              </label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-gray-900 text-white appearance-none"
                  {...register("gender", { required: "Gender is required" })}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            Address Information
          </h2>

          <div className="grid grid-cols-1 gap-6">
            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Address
              </label>
              <textarea
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none bg-gray-900 text-white placeholder-gray-500"
                rows="3"
                placeholder="Enter full address"
                {...register("address")}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                  placeholder="City"
                  {...register("city")}
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  State
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                  placeholder="State"
                  {...register("state")}
                />
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Pincode
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                  placeholder="123456"
                  {...register("pincode", {
                    pattern: {
                      value: /^\d{6}$/,
                      message: "Pincode must be 6 digits",
                    },
                  })}
                />
                {errors.pincode && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.pincode.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-400" />
            Academic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Enrollment Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Enrollment Date *
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-200 bg-gray-900 text-white"
                {...register("enrollmentDate", {
                  required: "Enrollment date is required",
                })}
              />
              {errors.enrollmentDate && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.enrollmentDate.message}
                </p>
              )}
            </div>

            {/* Previous School */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Previous School
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="Previous school name"
                {...register("previousSchool")}
              />
            </div>
          </div>

          {/* Subjects */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              Select Subjects
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-3 bg-gray-900 rounded-lg">
              {subjects.map((subject) => (
                <label
                  key={subject.id}
                  className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    value={subject.id}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                    {...register("selectedSubjects")}
                  />
                  <span className="text-sm text-gray-300">
                    {subject.subject}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Guardian Information */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-yellow-400" />
            Guardian Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Guardian Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Guardian Name *
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="Guardian name"
                {...register("guardianName", {
                  required: "Guardian name is required",
                })}
              />
              {errors.guardianName && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.guardianName.message}
                </p>
              )}
            </div>

            {/* Guardian Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Guardian Phone *
              </label>
              <input
                type="tel"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-200 bg-gray-900 text-white placeholder-gray-500"
                placeholder="+91 9876543210"
                {...register("guardianPhone", {
                  required: "Guardian phone is required",
                  pattern: {
                    value: /^[+]?[\d\s-]{10,}$/,
                    message: "Invalid phone number",
                  },
                })}
              />
              {errors.guardianPhone && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.guardianPhone.message}
                </p>
              )}
            </div>

            {/* Guardian Relation */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Relation
              </label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-200 bg-gray-900 text-white appearance-none"
                  {...register("guardianRelation")}
                >
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                  <option value="guardian">Guardian</option>
                  <option value="other">Other</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-700">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-br from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-green-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Registering Student...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Register Student
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => reset()}
            className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-gray-600 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:bg-gray-800 transition-all duration-200"
          >
            <RefreshCw className="w-5 h-5" />
            Reset Form
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default StudentRegistrationPage;
